import { type MetaFunction, json } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { RecentlyViewedService } from '#app/entities/car/recentlyViewedService.ts'
import { CarService } from '#app/entities/car/service.server.js'
import { getMetaTags } from '#app/shared/lib/meta-tags.ts'
import Card from '#app/shared/ui/data-display/Card.js'
import CarDescription from '#app/shared/ui/data-display/carDescription/CarDescription.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import { type IconName } from '#app/shared/ui/icons/name.js'
import { Button } from '#app/shared/ui/inputs/button/Button.tsx'
import { MainContainer } from '#app/shared/ui/layout/MainContainer.js'
import { Accordion } from '#app/shared/ui/surfaces/Accordion.js'
import Hero from './ui/Hero'

const accordionItems = [
  {
    trigger: '¿Cuáles son los requisitos mínimos para poder financiar?',
    content:
      'Para acceder a la financiación es necesario ser mayor de edad, disponer de un documento de identidad en vigor y contar con ingresos demostrables. Además, se evaluará la solvencia económica del solicitante para garantizar que la financiación se adapta a su situación. Todo el proceso es sencillo y te acompañamos en cada paso para que puedas completar la solicitud de forma rápida y segura.',
  },
  {
    trigger: '¿Qué ocurre si reservo un coche y me deniegan la financiación?',
    content: (
      <>
        Si finalmente la financiación no es aprobada,{' '}
        <span className="font-bold">no te preocupes</span>. La reserva se
        cancela sin penalización y se te devuelve íntegramente el importe
        abonado. Además, si lo deseas, podemos ayudarte a estudiar alternativas
        para que no pierdas la oportunidad de encontrar tu coche ideal.
      </>
    ),
  },
  {
    trigger: '¿Cuál es el importe máximo a financiar?',
    content: (
      <>
        El importe máximo a financiar dependerá del perfil del cliente, del
        vehículo elegido y de la entidad financiera que apruebe la operación. En
        general, es posible financiar hasta el{' '}
        <span className="font-bold">100% del valor del vehículo</span>,
        incluyendo determinados servicios asociados, siempre sujeto a estudio y
        aprobación final. Durante el proceso te informaremos de forma clara del
        importe máximo disponible para tu caso concreto.
      </>
    ),
  },
  {
    trigger:
      '¿A qué plazo máximo y mínimo se puede contratar la financiación? ',
    content: (
      <>
        Puedes financiar tu vehículo en un plazo flexible que va desde{' '}
        <span className="font-bold">36 hasta 120 meses</span>, adaptando la
        cuota mensual a tus necesidades. Además, la financiación permite{' '}
        <span className="font-bold">
          amortizar total o parcialmente desde el primer día
        </span>
        , para que puedas reducir capital o cancelar el préstamo cuando quieras,
        según las condiciones del contrato.
      </>
    ),
  },
]
type Props = {}

const MAX_CARS = 8

export async function loader({ request }: { request: Request }) {
  const recentSlugs: string[] =
    await RecentlyViewedService.getRecentlyViewedSlugsFromRequest(request)

  let cars = []

  if (recentSlugs.length > 0) {
    const slugsToFetch = recentSlugs.slice(0, MAX_CARS)

    const recentCars = await CarService.findMany({
      where: {
        slug: {
          in: slugsToFetch,
        },
      },
      select: {
        images: {
          select: {
            id: true,
            isPreviewImage: true,
          },
        },
        description: true,
        model: true,
        price: true,
        discount: true,
        year: true,
        mileage: true,
        minMonthlyPayment: true,
        minFinancedAmount: true,
        horsePower: true,
        transmission: true,
        fuelType: true,
        slug: true,
        brand: {
          select: {
            name: true,
          },
        },
      },
    })

    const slugOrder = new Map(slugsToFetch.map((slug, index) => [slug, index]))
    cars = recentCars.sort((a, b) => {
      const orderA = slugOrder.get(a.slug) ?? Number.MAX_SAFE_INTEGER
      const orderB = slugOrder.get(b.slug) ?? Number.MAX_SAFE_INTEGER
      return orderA - orderB
    })

    if (cars.length < MAX_CARS) {
      const existingSlugs = new Set(cars.map((car) => car.slug))
      const carsNeeded = MAX_CARS - cars.length

      const additionalCars = await CarService.findMany({
        where: {
          slug: {
            notIn: Array.from(existingSlugs),
          },
        },
        limit: carsNeeded,
        select: {
          images: {
            select: {
              id: true,
              isPreviewImage: true,
            },
          },
          description: true,
          model: true,
          price: true,
          discount: true,
          year: true,
          mileage: true,
          horsePower: true,
          transmission: true,
          minMonthlyPayment: true,
          minFinancedAmount: true,
          fuelType: true,
          slug: true,
          brand: {
            select: {
              name: true,
            },
          },
        },
      })

      cars = [...cars, ...additionalCars]
    }
  } else {
    cars = await CarService.findMany({
      limit: MAX_CARS,
      select: {
        images: {
          select: {
            id: true,
            isPreviewImage: true,
          },
        },
        description: true,
        model: true,
        price: true,
        discount: true,
        year: true,
        mileage: true,
        minMonthlyPayment: true,
        minFinancedAmount: true,
        horsePower: true,
        transmission: true,
        fuelType: true,
        slug: true,
        brand: {
          select: {
            name: true,
          },
        },
      },
    })
  }

  return json({ cars })
}

const cardsInfo = [
  {
    icon: 'basic/credit-card-check',
    title: 'Rápida, confidencial y sin complicaciones.',
    description:
      'Realiza el proceso en minutos desde nuestra página web, adaptando la financiación a tus necesidades. En breve un agente de Gama Óptima se pondrá en contacto contigo para formalizar tu petición solicitándote tus datos.',
  },
  {
    icon: 'basic/file-shield',
    title: 'Seguridad y aprobación en el día.',
    description:
      'Presenta tu DNI, nómina y documentos básicos, y deja que nuestro equipo gestione tu financiación sin riesgos ni sorpresas.',
  },
  {
    icon: 'basic/shield-dollar',
    title: 'Sin riesgos ni acceso a datos privados.',
    description:
      'Tus credenciales e historial bancario están protegidos en todo momento. La validación es totalmente confidencial y controlada por ti.',
  },
]

export const meta: MetaFunction = ({ matches }) => {
  return getMetaTags({
    matches,
    title: 'Financiación | Gama Óptima',
    url: 'https://gamaoptima.com/financia',
    index: false,
  })
}

export default function Financiacion({}: Props) {
  const { cars } = useLoaderData<typeof loader>()

  return (
    <div className="bg-neutral-25">
      {/* HERO */}
      <div
        className={`
        min-h-[500px] w-full bg-financia-hero bg-cover bg-center bg-no-repeat

        lg:min-h-[755px]
      `}
      >
        <Hero />
      </div>

      <MainContainer
        className={`
          px-4

          lg:px-4

          sm:px-14
        `}
      >
        {/* Título */}
        <p
          className={`
            mx-auto mt-12 max-w-[328px] text-center text-h4 font-bold
            text-neutral-900

            lg:mt-24 lg:text-h3

            md:max-w-[742px]
          `}
          style={{
            color: 'var(--neutral-900)',
          }}
        >
          Contáctanos sin compromiso al 931 318 618 y descubre cómo podemos
          adaptar el plan de financiación a tus necesidades.
        </p>
        <Spacer size="40px" />
        <div
          className={`
            flex flex-col items-center justify-between gap-4

            md:flex-row md:items-stretch
          `}
        >
          {cardsInfo.map((card) => (
            <Card
              key={card.title}
              icon={card.icon as IconName}
              title={card.title}
              description={card.description}
              className={`
                min-h-[165px] max-w-full bg-white

                md:min-h-[200px] md:flex-1
              `}
            />
          ))}
        </div>
        <Spacer size="72px" />
        <div
          className={`
            flex flex-col items-center gap-12

            lg:flex-row
          `}
        >
          <img
            src="/img/financia-horizonte-look.png"
            alt="Financia mirada"
            className={`
              w-full object-contain

              lg:max-w-[50%]

              md:max-w-[660px]
            `}
          />
          <div
            className={`
              flex flex-col items-center justify-center gap-4

              lg:items-start
            `}
          >
            <p
              className={`
                max-w-80 text-center text-h3 text-neutral-900

                lg:text-left

                md:max-w-[393px]
              `}
            >
              ¡Nos encargamos de todo para que no te preocupes!
            </p>

            <p
              className={`
                text-center text-subtitle1-medium text-neutral-500

                md:text-left
              `}
            >
              Te ofrecemos soluciones de financiación flexibles y claras,
              adaptadas a tu presupuesto y a tus necesidades. Podrás elegir
              entre opciones de financiación online rápidas o procesos
              convencionales totalmente seguros, con cuotas mensuales ajustadas
              y condiciones transparentes. Nuestro equipo te acompaña en todo el
              proceso para que financiar tu coche sea fácil, cómodo y sin
              sorpresas.
            </p>
            <Link to="/contacto?contactType=finance">
              <Button variant="primary" className={`text-nowrap`}>
                Déjanos tu mensaje
              </Button>
            </Link>
          </div>
        </div>
        <Spacer size="72px" />
        <div className="flex w-full justify-center">
          <Accordion
            accordionItems={accordionItems}
            variant="bordered-faq"
            className={`
              flex w-full max-w-[394px] flex-col gap-4 bg-neutral-25

              md:max-w-full
            `}
          />
        </div>
        <Spacer size="80px" />
        <CarDescription name="Vistos recientemente" cars={cars as any} />
        <Spacer size="40px" />
      </MainContainer>
    </div>
  )
}
